import React, { useCallback, useMemo, useState, type PropsWithChildren, Fragment } from 'react';

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, useMutation, graphql } from 'react-relay';
import { v4 as uuid } from 'uuid';
import { fg } from '@atlassian/jira-feature-gating';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import { createMutationErrorHandlers } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/utils/extract-error-message.tsx';

import type { ConfluencePagesEditViewProps } from '@atlassian/jira-issue-field-confluence-pages-editview-full/src/ui/confluence-pages/types.tsx';
import ConfluencePagesEditViewEntryPoint from '@atlassian/jira-issue-field-confluence-pages-editview-full/src/entrypoint.tsx';
import { ConfluencePagesReadView } from '@atlassian/jira-issue-field-confluence-pages-readview-full/src/ui/confluence-pages/index.tsx';
import type { SmartLinkOption } from '@atlassian/jira-issue-smart-links-popup-edit-view/src/common/types.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import type { confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditView_fragmentRef$key as ConfluencePagesFragment } from './__generated__/confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditView_fragmentRef.graphql';
import type { confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditViewWithIsEditable_fragmentRef$key as ConfluencePagesWithIsEditableFragment } from './__generated__/confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditViewWithIsEditable_fragmentRef.graphql';
import type { confluencePages_issueFieldConfluencePagesInlineEditFull_Mutation as ConfluencePagesMutation } from './__generated__/confluencePages_issueFieldConfluencePagesInlineEditFull_Mutation.graphql';
import type {
	ConfluencePagesInlineEditViewProps,
	ConfluencePagesInlineEditViewWithIsEditableProps,
} from './types.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-confluence-pages-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/** Determine if the updated edit view value is equal to our original value before submitting a mutation. */
const isEqualOption = (a: SmartLinkOption[], b: SmartLinkOption[]) => {
	if (a.length !== b.length) return false;

	const aIdSet = new Set(a.map((option) => option.url));
	return b.every((option) => aIdSet.has(option.url));
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `ConfluencePagesInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [ConfluencePagesInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const ConfluencePagesInlineEditViewWithIsEditable = ({
	editViewPopup,
	editViewPopupAlignBlock,
	fragmentRef,
	isEditable,
	isEditing: startWithEditViewOpen = false,
	menuPosition,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	readViewFitContainerHeight,
	spacing = 'compact',
	isTruncated,
}: ConfluencePagesInlineEditViewWithIsEditableProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<ConfluencePagesWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditViewWithIsEditable_fragmentRef on JiraConfluenceRemoteIssueLinksField
			@argumentDefinitions(
				isJiraConfluencePageContentEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-confluence-page-content-enabled.relayprovider"
				}
			) {
				id
				name
				type
				...confluencePages_issueFieldConfluencePagesReadviewFull_ConfluencePagesReadView
				confluenceRemoteIssueLinks(first: 1000) @optIn(to: "JiraConfluenceRemoteIssueLinksField") {
					edges {
						node {
							id
							href
							pageContent @include(if: $isJiraConfluencePageContentEnabled) {
								... on JiraConfluencePageContentDetails {
									href
								}
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<ConfluencePagesMutation>(graphql`
		mutation confluencePages_issueFieldConfluencePagesInlineEditFull_Mutation(
			$input: JiraUpdateConfluenceRemoteIssueLinksFieldInput!
			$isJiraConfluencePageContentEnabled: Boolean!
		) @raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updateConfluenceRemoteIssueLinksField(input: $input) {
					success
					errors {
						message
					}
					field {
						...confluencePages_issueFieldConfluencePagesReadviewFull_ConfluencePagesReadView
						confluenceRemoteIssueLinks(first: 1000)
							@optIn(to: "JiraConfluenceRemoteIssueLinksField") {
							edges {
								node {
									id
									href
									pageContent @include(if: $isJiraConfluencePageContentEnabled) {
										... on JiraConfluencePageContentDetails {
											href
										}
									}
								}
							}
						}
					}
				}
			}
		}
	`);
	// #endregion

	// #region Common state
	const { id: uniqueFieldId, name: fieldName, type: fieldType, confluenceRemoteIssueLinks } = data;

	const initialValue = useMemo(
		(): SmartLinkOption[] =>
			confluenceRemoteIssueLinks?.edges
				?.map((edge) => edge?.node)
				.filter(Boolean)
				.map((node) => {
					const url = fg('jira_preview_panel_m_1_rollout')
						? (node.pageContent?.href ?? node.href ?? '')
						: (node.href ?? '');
					return {
						id: node.id,
						url,
					};
				}) ?? [],
		[confluenceRemoteIssueLinks],
	);
	const [updatedValue, setUpdatedValue] = useState<SmartLinkOption[]>(initialValue);

	const handleSubmit = useCallback(
		(value: SmartLinkOption[], { onSuccess, onFail }: OnSubmitCallbacks) => {
			onSubmit?.(value);

			const onCompleted = fg('thor_inline-edit-actions-error-extraction')
				? createMutationErrorHandlers({ onSuccess, onFail })
				: // eslint-disable-next-line @typescript-eslint/no-explicit-any
					(response: any) => {
						if (response.jira?.updateConfluenceRemoteIssueLinksField?.success) {
							onSuccess();
						} else {
							onFail();
						}
					};

			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operations: [
							{
								// Retain the ID of existing linked pages and use the URL for new links to add.
								links: value.map(({ id, url }) => (id ? { id } : { href: url })),
								operation: 'SET',
							},
						],
					},
					isJiraConfluencePageContentEnabled: fg('jira_preview_panel_m_1_rollout'),
				},
				onCompleted,
				onError(error) {
					onFail(error);
				},
				optimisticResponse: {
					jira: {
						updateConfluenceRemoteIssueLinksField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								confluenceRemoteIssueLinks: {
									totalCount: value.length,
									edges: value.map((option) => ({
										node: {
											id: option.id ?? uuid(),
											href: option.url,
											pageContent: fg('jira_preview_panel_m_1_rollout')
												? {
														__typename: 'JiraConfluencePageContentDetails',
														href: option.url,
													}
												: null,
										},
									})),
								},
							},
						},
					},
				},
			});
		},
		[commit, onSubmit, uniqueFieldId],
	);

	const {
		invalidMessage,
		isEditing,
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChange,
	} = useFieldInlineEditActions({
		fieldId: uniqueFieldId,
		fieldName,
		fieldType,
		initialValue,
		isValueEqual: isEqualOption,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		startWithEditViewOpen,
		updatedValue,
	});

	// #region Read view
	const renderReadView = () => (
		<MaybeUFOSegment>
			<ConfluencePagesReadView isTruncated={isTruncated} fragmentRef={data} />
		</MaybeUFOSegment>
	);
	// #endregion

	// #region Edit view
	const getEditViewProps = (fieldProps: ValidationFieldProps): ConfluencePagesEditViewProps => ({
		...fieldProps,
		initialIsOpen: true,
		value: updatedValue,
		menuPosition,
		onChange: handleChange,
		spacing,
	});
	// #endregion

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewEntryPoint={ConfluencePagesEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hasReadViewTruncateDisabled={isTruncated}
			hideActionButtons
		/>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [ConfluencePagesInlineEditViewProps](./types.tsx)
 */
export const ConfluencePagesInlineEditView = ({
	fragmentRef,
	...props
}: ConfluencePagesInlineEditViewProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<ConfluencePagesFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditView_fragmentRef on JiraConfluenceRemoteIssueLinksField {
				...confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);
	// #endregion

	return (
		<ConfluencePagesInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
